import apiClient from './apiClient';

export interface AdminAlert {
  id: string;
  type: 'dispute' | 'verification' | 'payment' | 'leakage';
  title: string;
  description: string;
  severity: 'high' | 'medium' | 'low';
  created_at: string;
  route: string; // where to navigate when clicked
}

export interface AlertCounts {
  disputes: number;
  verifications: number;
  payments: number;
  total: number;
}

const adminAlertsService = {
  async getCounts(): Promise<AlertCounts> {
    const [disputes, verifications, payments] = await Promise.allSettled([
      apiClient.get('/admin/disputes/', { params: { status: 'open', limit: 1, page: 1 } }),
      apiClient.get('/admin/verifications/', { params: { status: 'pending', limit: 1, page: 1 } }),
      apiClient.get('/admin/withdrawals/', { params: { status: 'pending', limit: 1, page: 1 } }),
    ]);

    const getCount = (res: PromiseSettledResult<any>): number => {
      if (res.status === 'rejected') return 0;
      const d = res.value?.data;
      return d?.total ?? d?.count ?? (Array.isArray(d?.results) ? -1 : 0);
    };

    // If total is -1 (no pagination), try results.length
    const disputeCount = (() => {
      if (disputes.status === 'rejected') return 0;
      const d = disputes.value?.data;
      return d?.total ?? d?.count ?? d?.results?.length ?? 0;
    })();
    const verificationCount = (() => {
      if (verifications.status === 'rejected') return 0;
      const d = verifications.value?.data;
      return d?.total ?? d?.count ?? d?.results?.length ?? 0;
    })();
    const paymentCount = (() => {
      if (payments.status === 'rejected') return 0;
      const d = payments.value?.data;
      return d?.total ?? d?.count ?? d?.results?.length ?? 0;
    })();

    return {
      disputes: disputeCount,
      verifications: verificationCount,
      payments: paymentCount,
      total: disputeCount + verificationCount + paymentCount,
    };
  },

  async getAlerts(): Promise<AdminAlert[]> {
    const alerts: AdminAlert[] = [];

    // Open disputes
    try {
      const res = await apiClient.get('/admin/disputes/', {
        params: { status: 'open', limit: 20, page: 1 },
      });
      const results = res.data?.results ?? res.data ?? [];
      for (const d of results) {
        alerts.push({
          id: `dispute-${d.id}`,
          type: 'dispute',
          title: `Open Dispute — ${d.job?.title ?? 'Job'}`,
          description: `Raised by ${d.initiated_by?.name ?? 'user'}: ${(d.reason ?? '').substring(0, 120)}`,
          severity: 'high',
          created_at: d.created_at,
          route: '/disputes',
        });
      }
    } catch { /* non-fatal */ }

    // Pending verifications
    try {
      const res = await apiClient.get('/admin/verifications/', {
        params: { status: 'pending', limit: 20, page: 1 },
      });
      const results = res.data?.results ?? res.data ?? [];
      for (const v of results) {
        alerts.push({
          id: `verification-${v.id}`,
          type: 'verification',
          title: `Verification Pending — ${v.artisan?.name ?? 'Artisan'}`,
          description: `${v.type?.toUpperCase() ?? 'KYC'} verification awaiting review`,
          severity: 'medium',
          created_at: v.created_at ?? v.submitted_at,
          route: '/verifications',
        });
      }
    } catch { /* non-fatal */ }

    // Pending withdrawals
    try {
      const res = await apiClient.get('/admin/withdrawals/', {
        params: { status: 'pending', limit: 20, page: 1 },
      });
      const results = res.data?.results ?? res.data ?? [];
      for (const w of results) {
        alerts.push({
          id: `payment-${w.id}`,
          type: 'payment',
          title: `Withdrawal Request — ₦${Number(w.amount ?? 0).toLocaleString()}`,
          description: `${w.artisan?.name ?? w.user?.name ?? 'Artisan'} requested a payout`,
          severity: 'medium',
          created_at: w.created_at,
          route: '/payments',
        });
      }
    } catch { /* non-fatal */ }

    // Sort newest first
    return alerts.sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  },
};

export default adminAlertsService;
